// @flow

import { beginRequest, completeRequest } from "@store/utils";
import type { ReasonSpecificScriptsState } from "./types";
import type { Action } from "redux-action";
import type { ApprovableContainer } from "@modules/service/types";
import type { ReasonSpecificScript } from "@modules/service/reducer/reason-specific-scripts/types";

export type ShowReasonSpecificScriptModalRequest = $Exact<{
  show: boolean,
  step?: number,
}>;

const startRequest = beginRequest(["requestsInProgress"]);
const finishRequest = completeRequest(["requestsInProgress"]);

export const onGetReasonSpecificScripts = (state: ReasonSpecificScriptsState) => () =>
  startRequest(state);

export const onGetReasonSpecificScriptsSuccess = (state: ReasonSpecificScriptsState) => ({
  payload,
}: Object) => {
  return {
    ...state,
    requestsInProgress: state.requestsInProgress > 0 ? --state.requestsInProgress : 0,
    list: {
      ...state.list,
      ...payload,
    },
  };
};

export const onNewReasonSpecificScript = (state: ReasonSpecificScriptsState) => (
  action: Action<void>,
) => ({
  ...state,
  draft: {
    scriptText: null,
    scriptName: null,
    clientAbsenceReasons: null,
    isActive: null,
  },
});

export const onSetReasonSpecificScriptDraft = (state: ReasonSpecificScriptsState) => ({
  payload,
}: Object) => ({
  ...state,
  current: payload,
});

export const onNewReasonSpecificScriptAssignments = (state: ReasonSpecificScriptsState) => (
  action: Action<void>,
) => ({
  ...state,
  assignments: [],
});

export const onShowAddReasonSpecificScriptModal = (state: ReasonSpecificScriptsState) => ({
  payload,
}: Action<ShowReasonSpecificScriptModalRequest>) => {
  if (!payload) {
    return state;
  }

  return {
    ...state,
    modals: {
      ...state.modals,
      showAddReasonSpecificScriptModal: payload.show,
      modalStep: payload.step || state.modals.modalStep,
    },
  };
};

export const onUpdateReasonSpecificScript = (state: ReasonSpecificScriptsState) => ({
  payload,
}: Object) => ({
  ...state,
  ...startRequest(state),
  draft: payload,
});

export const onUpdateReasonSpecificScriptSuccess = (state: ReasonSpecificScriptsState) => ({
  payload,
}: Object) => finishRequest(state);

export const onUpdateReasonSpecificScriptError = (state: ReasonSpecificScriptsState) => ({
  payload,
}: Object) => finishRequest(state);

export const onUpdateReasonSpecificScriptDraft = (state: ReasonSpecificScriptsState) => ({
  payload,
}: Object) => ({
  ...state,
  draft: payload,
});

export const onGetReasonSpecificScriptActiveSuccess = (state: ReasonSpecificScriptsState) => ({
  payload,
}: Object) => {
  return {
    ...state,
    requestsInProgress: state.requestsInProgress > 0 ? --state.requestsInProgress : 0,
    current: {
      ...state.current,
      ...payload,
    },
  };
};

export const onSetReasonSpecificScriptAssignments = (state: ReasonSpecificScriptsState) => ({
  payload,
}: Object) => ({
  ...state,
  assignments: payload,
});

export const onSetReasonSpecificScriptDraftAssignments = (state: ReasonSpecificScriptsState) => ({
  payload,
}: Object) => ({
  ...state,
  draftAssignments: payload,
});

export const onGetReasonSpecificScript = (state: ReasonSpecificScriptsState) => () =>
  startRequest(state);

export const onGetReasonSpecificScriptSuccess = (state: ReasonSpecificScriptsState) => ({
  payload,
}: Object) => {
  return {
    ...state,
    ...finishRequest(state),
    current: {
      ...state.current,
      ...payload,
    },
  };
};
export const onGetReasonSpecificScriptError = (state: ReasonSpecificScriptsState) => () =>
  finishRequest(state);

export const onShowReasonSpecificScriptEditModal = (state: ReasonSpecificScriptsState) => ({
  payload,
}: Object) => ({
  ...state,
  modals: {
    ...state.modals,
    showEditModal: payload,
  },
});

export const onSetShowSubmitReasonSpecificScriptModal = (state: ReasonSpecificScriptsState) => ({
  payload,
}: Object) => ({
  ...state,
  modals: {
    ...state.modals,
    showSubmitModal: payload,
  },
});

export const onSetShowCancelReasonSpecificScriptModal = (state: ReasonSpecificScriptsState) => ({
  payload,
}: Object) => ({
  ...state,
  modals: {
    ...state.modals,
    showCancelModal: payload,
  },
});

export const onStartReasonSpecificScriptDraftSuccess = (state: ReasonSpecificScriptsState) => (
  action: Action<ApprovableContainer<ReasonSpecificScript>>,
) => ({
  ...state,
  draft: {
    ...((action.payload && action.payload.active) || {}),
  },
});

export const onSetShowDiscardReasonSpecificScriptModal = (state: ReasonSpecificScriptsState) => ({
  payload,
}: Object) => ({
  ...state,
  modals: {
    ...state.modals,
    showDiscardModal: payload,
  },
});

export const onSetShowCancelDiscardReasonSpecificScriptModal = (
  state: ReasonSpecificScriptsState,
) => ({ payload }: Object) => ({
  ...state,
  modals: {
    ...state.modals,
    showCancelDiscardModal: payload,
  },
});

export const onDuplicateReasonSpecificScriptSuccess = (state: ReasonSpecificScriptsState) => (
  action: Action<ReasonSpecificScript>,
) => ({
  ...state,
  draft: {
    clientAbsenceReasons: (action.payload || {}).clientAbsenceReasons,
    scriptName: (action.payload || {}).scriptName,
    scriptText: (action.payload || {}).scriptText,
  },
});

export const onSetShowDisableReasonSpecificScriptModal = (state: ReasonSpecificScriptsState) => ({
  payload,
}: Object) => ({
  ...state,
  modals: {
    ...state.modals,
    showDisableModal: payload,
  },
});

export const onShowEditReasonSpecificScriptAssignmentsModal = (
  state: ReasonSpecificScriptsState,
) => ({ payload }: Object) => ({
  ...state,
  modals: {
    ...state.modals,
    showEditAssignmentsModal: payload,
  },
});
